import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Tooltip,
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    PlayArrow as ActivityIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { Child } from '../../types';
import { childrenService } from '../../services/api';
import CountrySelector from '../CountrySelector';
import { useLanguage } from '../../contexts/LanguageContext';

interface ChildrenListProps {
    children: Child[];
}

const ChildrenList: React.FC<ChildrenListProps> = ({ children: initialChildren }) => {
    const [children, setChildren] = useState<Child[]>(initialChildren);
    const [open, setOpen] = useState(false);
    const [editingChild, setEditingChild] = useState<Child | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [childToDelete, setChildToDelete] = useState<Child | null>(null);
    const navigate = useNavigate();
    const { language } = useLanguage();
    const t = (es: string, en: string) => language === 'es' ? es : en;

    const validationSchema = yup.object({
        full_name: yup.string().required(t('El nombre completo es requerido', 'Full name is required')),
        birth_date: yup.date().required(t('La fecha de nacimiento es requerida', 'Birth date is required')),
        country: yup.string().required(t('El país de residencia es requerido', 'Country of residence is required')),
    });

    useEffect(() => {
        setChildren(initialChildren);
    }, [initialChildren]);

    const formik = useFormik({
        initialValues: {
            full_name: '',
            birth_date: null as Date | null,
            country: '',
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            try {
                if (editingChild) {
                    const updatedChild = await childrenService.updateChild(editingChild.id, {
                        ...values,
                        birth_date: values.birth_date?.toISOString().split('T')[0] || '',
                    });
                    const updatedChildren = children.map(child => 
                        child.id === updatedChild.id ? updatedChild : child
                    );
                    setChildren(updatedChildren);
                    
                    // Actualizar el localStorage
                    const dashboardData = localStorage.getItem('dashboardData');
                    if (dashboardData) {
                        const data = JSON.parse(dashboardData);
                        const updatedData = { ...data, children: updatedChildren };
                        localStorage.setItem('dashboardData', JSON.stringify(updatedData));
                    }
                } else {
                    const newChild = await childrenService.createChild({
                        ...values,
                        birth_date: values.birth_date?.toISOString().split('T')[0] || '',
                    });
                    const updatedChildren = [...children, newChild];
                    setChildren(updatedChildren);
                    
                    // Actualizar el localStorage
                    const dashboardData = localStorage.getItem('dashboardData');
                    if (dashboardData) {
                        const data = JSON.parse(dashboardData);
                        const updatedData = { ...data, children: updatedChildren };
                        localStorage.setItem('dashboardData', JSON.stringify(updatedData));
                    }
                }
                handleClose();
            } catch (error) {
                console.error('Error al guardar el niño:', error);
            }
        },
    });

    const handleClickOpen = () => {
        setEditingChild(null);
        formik.resetForm();
        setOpen(true);
    };

    const handleEdit = (child: Child) => {
        setEditingChild(child);
        formik.setValues({
            full_name: child.full_name,
            birth_date: new Date(child.birth_date),
            country: child.country || '',
        });
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setEditingChild(null);
        formik.resetForm();
    };

    const handleDeleteClick = (child: Child) => {
        setChildToDelete(child);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!childToDelete) return;
        
        try {
            await childrenService.deleteChild(childToDelete.id);
            const updatedChildren = children.filter(child => child.id !== childToDelete.id);
            setChildren(updatedChildren);
            
            // Actualizar el localStorage
            const dashboardData = localStorage.getItem('dashboardData');
            if (dashboardData) {
                const data = JSON.parse(dashboardData);
                const updatedData = { ...data, children: updatedChildren };
                localStorage.setItem('dashboardData', JSON.stringify(updatedData));
            }
            
            setDeleteDialogOpen(false);
            setChildToDelete(null);
        } catch (error) {
            console.error('Error al eliminar el niño:', error);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setChildToDelete(null);
    };

    const handleViewActivity = (child: Child) => {
        navigate(`/actividad/${child.id}`);
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">{t('Lista de Niños', 'Children List')}</Typography>
                <Button variant="contained" onClick={handleClickOpen}>
                    {t('Agregar Niño', 'Add Child')}
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('Nombre', 'Name')}</TableCell>
                            <TableCell>{t('Fecha de Nacimiento', 'Birth Date')}</TableCell>
                            <TableCell>{t('País de residencia', 'Country of Residence')}</TableCell>
                            <TableCell align="right" sx={{ width: '120px' }}>{t('Acciones', 'Actions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {children.map((child) => (
                            <TableRow key={child.id}>
                                <TableCell>{child.full_name}</TableCell>
                                <TableCell>{new Date(child.birth_date).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US')}</TableCell>
                                <TableCell>{child.country}</TableCell>
                                <TableCell align="right">
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                        <Tooltip title={t('Ver Actividad', 'View Activity')}>
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() => handleViewActivity(child)}
                                            >
                                                <ActivityIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={t('Editar', 'Edit')}>
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() => handleEdit(child)}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={t('Eliminar', 'Delete')}>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => handleDeleteClick(child)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>
                    {editingChild ? t('Editar Niño', 'Edit Child') : t('Agregar Niño', 'Add Child')}
                </DialogTitle>
                <DialogContent>
                    <Box component="form" onSubmit={formik.handleSubmit} sx={{ mt: 1 }}>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="full_name"
                            label={t('Nombre Completo', 'Full Name')}
                            name="full_name"
                            value={formik.values.full_name}
                            onChange={formik.handleChange}
                            error={formik.touched.full_name && Boolean(formik.errors.full_name)}
                            helperText={formik.touched.full_name && formik.errors.full_name}
                        />
                        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={language === 'es' ? es : undefined}>
                            <DatePicker
                                label={t('Fecha de Nacimiento', 'Birth Date')}
                                value={formik.values.birth_date}
                                onChange={(date) => formik.setFieldValue('birth_date', date)}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        margin: 'normal',
                                        error: formik.touched.birth_date && Boolean(formik.errors.birth_date),
                                        helperText: formik.touched.birth_date && formik.errors.birth_date,
                                    },
                                }}
                            />
                        </LocalizationProvider>
                        <CountrySelector
                            value={formik.values.country}
                            onChange={(value) => formik.setFieldValue('country', value)}
                            label={t('País de residencia', 'Country of Residence')}
                            placeholder={t('Selecciona el país', 'Select the country')}
                            required
                            error={formik.touched.country && Boolean(formik.errors.country)}
                            helperText={formik.touched.country && formik.errors.country ? (formik.errors.country as string) : undefined}
                            sx={{ mt: 2 }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>{t('Cancelar', 'Cancel')}</Button>
                    <Button onClick={() => formik.handleSubmit()} variant="contained">
                        {editingChild ? t('Guardar', 'Save') : t('Agregar', 'Add')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog 
                open={deleteDialogOpen} 
                onClose={handleDeleteCancel}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        border: '2px solid #f44336'
                    }
                }}
            >
                <DialogTitle sx={{ 
                    textAlign: 'center', 
                    color: '#f44336',
                    fontWeight: 700,
                    fontSize: '1.5rem'
                }}>
                    ⚠️ {t('Confirmar Eliminación', 'Confirm Deletion')}
                </DialogTitle>
                <DialogContent sx={{ textAlign: 'center', py: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ color: '#333', mb: 2 }}>
                        {t('¿Estás seguro de que deseas eliminar a', 'Are you sure you want to delete')} <strong>{childToDelete?.full_name}</strong>?
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#666', mb: 2 }}>
                        {t('Esta acción no se puede deshacer. Se eliminarán todos los datos asociados al niño, incluyendo:', 'This action cannot be undone. All data associated with the child will be deleted, including:')}
                    </Typography>
                    <Box sx={{ 
                        background: '#fff3e0', 
                        p: 2, 
                        borderRadius: 2, 
                        border: '1px solid #ffb74d',
                        mb: 2
                    }}>
                        <Typography variant="body2" sx={{ color: '#e65100', fontWeight: 600 }}>
                            • {t('Información personal del niño', 'Child\'s personal information')}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#e65100', fontWeight: 600 }}>
                            • {t('Actividades subidas', 'Uploaded activities')}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#e65100', fontWeight: 600 }}>
                            • {t('Progreso en los libros desbloqueados', 'Progress in unlocked books')}
                        </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: '#f44336', fontWeight: 600 }}>
                        {t('Esta acción es permanente y no se puede revertir.', 'This action is permanent and cannot be reversed.')}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', pb: 3, px: 3 }}>
                    <Button 
                        onClick={handleDeleteCancel}
                        variant="outlined"
                        sx={{ 
                            borderRadius: 2,
                            fontWeight: 600,
                            px: 3
                        }}
                    >
                        {t('Cancelar', 'Cancel')}
                    </Button>
                    <Button 
                        onClick={handleDeleteConfirm} 
                        variant="contained" 
                        color="error"
                        sx={{ 
                            borderRadius: 2,
                            fontWeight: 600,
                            px: 3,
                            '&:hover': {
                                backgroundColor: '#d32f2f'
                            }
                        }}
                    >
                        {t('Sí, Eliminar', 'Yes, Delete')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ChildrenList; 